import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useUIStore, useWorkspaceStore } from '../store/index';
import { api, getApiErrorMessage } from '../lib/api';
import { agentAvatarDataUri, ownerColor } from '../lib/agentAvatar';
import { resolveLocale } from '../lib/dateFormat';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import Team from './Team';
import { DASHBOARD_WIDGETS, MAX_DASHBOARD_WIDGETS } from '../constants/dashboardWidgets';
import { WorkspaceGroup, ImpactDomain, WorkspaceInvitation, Workspace, UserRole, Project, ProjectMember, ProjectInvitation, User } from '../types';
import {
  UserPlus,
  Users,
  Globe,
  AlertTriangle,
  Folder,
  Plus,
  Save,
  Trash2,
  Edit2,
  XCircle,
  LayoutTemplate
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialogProvider';

function buildAgentGuide(params: {
  agentName: string;
  agentId: string;
  workspaceName: string;
  groupNames: string[];
  scopes: string[];
  token: string;
  t: (key: string) => string;
}): string {
  const baseUrl = `${window.location.origin}/api`;
  return `# ${params.t('settings.agents.guide.title')} — ${params.agentName}

${params.workspaceName} — ${params.t('settings.agents.guide.memberOf')}: ${params.groupNames.join(', ')}

## ${params.t('settings.agents.guide.authTitle')}
Authorization: Bearer ${params.token}

## ${params.t('settings.agents.guide.scopesTitle')}
${params.scopes.join(', ')}

## ${params.t('settings.agents.guide.rateLimitTitle')}
${params.t('settings.agents.guide.rateLimitText')}

## ${params.t('settings.agents.guide.tasksTitle')}
GET ${baseUrl}/tasks
${params.t('settings.agents.guide.tasksListHint')}
assigned_to_user_id = ${params.agentId}

PUT ${baseUrl}/tasks/:id/claim
POST ${baseUrl}/tasks/:id/notes     body: { "note": "..." }   <- ${params.t('settings.agents.guide.commentsHint')}
PUT ${baseUrl}/tasks/:id/complete   body: { "note": "..." }
PUT ${baseUrl}/tasks/:id/reassign   body: { "assigned_to_user_id" or "assigned_to_group_id", "note": "..." }   <- ${params.t('settings.agents.guide.reassignOrderHint')}
POST ${baseUrl}/tasks/:id/messages  (multipart/form-data, field "message")   <- ${params.t('settings.agents.guide.messagesHint')}

## ${params.t('settings.agents.guide.incidentsTitle')}
GET ${baseUrl}/incidents
PUT ${baseUrl}/incidents/:id/start   body: { "assigned_group_id", "investigation_notes" }
POST ${baseUrl}/incidents/:id/comments   body: { "message": "..." }
PUT ${baseUrl}/incidents/:id/close
PUT ${baseUrl}/incidents/:id/reassign   body: { "assigned_group_id", "note": "..." }

## ${params.t('settings.agents.guide.documentsTitle')}
${params.t('settings.agents.guide.documentsProjectIdHint')}
GET ${baseUrl}/projects/:projectId/documents
POST ${baseUrl}/projects/:projectId/documents   (multipart/form-data, field "file")
GET ${baseUrl}/projects/:projectId/documents/:documentId/download
DELETE ${baseUrl}/projects/:projectId/documents/:documentId

## ${params.t('settings.agents.guide.exampleTitle')}
curl -H "Authorization: Bearer ${params.token}" ${baseUrl}/tasks`;
}

interface TaskTemplate {
  id: string;
  name: string;
  title: string;
  description: string;
  domain_id: string | null;
  project_id: string | null;
  assigned_to_user_id: string | null;
  assigned_to_group_id: string | null;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useUIStore();
  const confirm = useConfirm();
  const user = useAuthStore((s) => s.user);
  // Header.tsx already fetches /workspace/me into the shared store on app
  // mount, almost always before this page does — reuse it instead of an
  // always-duplicate fetch (see fetchWorkspace below), and keep it in sync
  // after a save so Sidebar's workspace name doesn't go stale until a
  // reload. Only the (referentially stable) action is subscribed to here
  // — fetchWorkspace reads the current value via getState() itself,
  // so its own identity doesn't change and re-trigger the effect below
  // every time the shared workspace updates.
  const setSharedWorkspace = useWorkspaceStore((s) => s.setWorkspace);
  const canManage = user && (user.role === 'superadmin' || user.role === 'manager');
  const isSuperAdmin = user?.role === 'superadmin';

  // Which settings section is showing in the right-hand panel — mirrors
  // the mockup's two-pane layout (sub-nav on the left, one section's
  // content on the right) instead of the old all-cards-at-once grid.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<
    'profile' | 'preferences' | 'templates' | 'homeCustomization' | 'invitations' | 'team' | 'domains' | 'projects' | 'apiDocs'
  >(() => {
    // Deep-link support for e.g. Home widget links (/settings?section=invitations).
    const section = searchParams.get('section');
    const valid = ['profile', 'preferences', 'templates', 'homeCustomization', 'invitations', 'team', 'domains', 'projects', 'apiDocs'];
    return valid.includes(section || '') ? (section as any) : 'profile';
  });

  // Keeps the URL in sync with the active section so a page refresh (or a
  // bookmark/share) lands back on the same tab instead of always resetting
  // to Profile.
  const handleSectionChange = (id: typeof activeSection) => {
    setActiveSection(id);
    setSearchParams({ section: id });
  };

  // My Profile — self-service fields via PUT /auth/me (name/job_title/
  // phone/timezone/notification_preference only; role/group/workspace
  // stay admin-only through the existing PUT /users/:id elsewhere).
  const [profileName, setProfileName] = useState('');
  const [profileJobTitle, setProfileJobTitle] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileTimezone, setProfileTimezone] = useState('');
  const [notificationPreference, setNotificationPreference] = useState<'email' | 'in_app'>('email');
  const [reportLanguage, setReportLanguage] = useState<'en' | 'tr' | 'es'>('en');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfileName(user.name || '');
    setProfileJobTitle(user.job_title || '');
    setProfilePhone(user.phone || '');
    setProfileTimezone(user.timezone || '');
    setNotificationPreference(user.notification_preference || 'email');
    setReportLanguage(user.report_language || 'en');
  }, [user]);

  const saveMyProfile = async (fields: Record<string, any>) => {
    setProfileSaving(true);
    setProfileSaveError(null);
    setProfileSaveSuccess(false);
    try {
      const res = await api.put('/auth/me', fields);
      const updated = (res.data as any)?.data;
      if (updated) {
        useAuthStore.setState((prev) => ({ user: prev.user ? { ...prev.user, ...updated } : prev.user }));
      }
      setProfileSaveSuccess(true);
    } catch (err: any) {
      setProfileSaveError(getApiErrorMessage(err, 'Failed to save changes.'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSaveProfileDetails = () => saveMyProfile({
    name: profileName,
    job_title: profileJobTitle || null,
    phone: profilePhone || null,
    timezone: profileTimezone || null
  });

  const handleSaveNotificationPreference = (value: 'email' | 'in_app') => {
    setNotificationPreference(value);
    saveMyProfile({ notification_preference: value });
  };

  const handleSaveReportLanguage = (value: 'en' | 'tr' | 'es') => {
    setReportLanguage(value);
    saveMyProfile({ report_language: value });
  };

  // My Profile — change password (replaces the old email-a-reset-link
  // flow; uses the existing PUT /users/:id/password endpoint directly,
  // targeting the caller's own id).
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwChangeLoading, setPwChangeLoading] = useState(false);
  const [pwChangeMessage, setPwChangeMessage] = useState<string | null>(null);
  const [pwChangeError, setPwChangeError] = useState<string | null>(null);

  const handleChangePassword = async () => {
    if (!user?.id) return;
    setPwChangeMessage(null);
    setPwChangeError(null);
    if (newPassword !== confirmNewPassword) {
      setPwChangeError(t('settings.profile.changePassword.mismatch'));
      return;
    }
    setPwChangeLoading(true);
    try {
      await api.put(`/users/${user.id}/password`, { currentPassword, newPassword });
      setPwChangeMessage(t('settings.profile.changePassword.success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      const rawMessage = getApiErrorMessage(err, t('errors.general'));
      // The backend's Joi validators return this exact English string with
      // no localization of their own — swap in the already-translated
      // auth.passwordComplexity copy rather than showing raw English.
      const isComplexityError = /must contain at least one uppercase letter/i.test(rawMessage);
      setPwChangeError(isComplexityError ? t('auth.passwordComplexity') : rawMessage);
    } finally {
      setPwChangeLoading(false);
    }
  };

  const formatProfileDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const PROFILE_ROLE_KEYS: Record<string, string> = {
    superadmin: 'superadmin',
    manager: 'manager',
    operator: 'operator',
    client_user: 'clientUser'
  };

  // Invitation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('operator');
  const [inviteGroupId, setInviteGroupId] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Invitations list
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const [revokingInvitation, setRevokingInvitation] = useState<string | null>(null);

  // Agents state
  const [agentName, setAgentName] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [agentRole, setAgentRole] = useState<'operator' | 'client_user'>('operator');
  const [agentCreating, setAgentCreating] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [deactivatingAgent, setDeactivatingAgent] = useState<string | null>(null);
  const [regeneratingAgent, setRegeneratingAgent] = useState<string | null>(null);
  const [terminatingAgent, setTerminatingAgent] = useState<string | null>(null);
  // Inline scope picker for issuing an ADDITIONAL token on an existing
  // agent (e.g. to grant a new scope like documents:* without losing the
  // agent's identity/history, which Regenerate can't do since it only
  // reuses the union of already-active scopes). Keyed by agent id so only
  // one row's picker is open at a time.
  const [issuingTokenAgent, setIssuingTokenAgent] = useState<string | null>(null);
  const [issueTokenScopes, setIssueTokenScopes] = useState<string[]>([]);
  const [issuingToken, setIssuingToken] = useState(false);
  // Per-agent webhooks panel (docs/AGENT_API_SPEC.md §13) — same toggle/
  // cache pattern as the New-token picker above, one row open at a time.
  const [webhooksAgent, setWebhooksAgent] = useState<string | null>(null);
  const [agentWebhooks, setAgentWebhooks] = useState<Record<string, any[]>>({});
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState<string[]>([]);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  // Secret is shown exactly once, at creation — same rule as an api_tokens plaintext.
  const [createdWebhookSecret, setCreatedWebhookSecret] = useState<string | null>(null);
  const [deletingWebhookId, setDeletingWebhookId] = useState<string | null>(null);
  // Recent delivery attempts for one webhook row, expanded on demand.
  const [expandedDeliveriesWebhook, setExpandedDeliveriesWebhook] = useState<string | null>(null);
  const [webhookDeliveries, setWebhookDeliveries] = useState<Record<string, any[]>>({});
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  // Token plaintext is shown exactly once and cleared when the modal closes — never persisted.
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [agentPlaintextToken, setAgentPlaintextToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Collapsed by default — the form only opens on "+ Add Your Agent" (2026-10-18 user feedback).
  const [addAgentExpanded, setAddAgentExpanded] = useState(false);
  // Snapshot of the just-created agent, kept so the token modal can render its guide.
  const [createdAgentInfo, setCreatedAgentInfo] = useState<{ id: string; name: string; groupNames: string[]; scopes: string[] } | null>(null);
  const [guideExpanded, setGuideExpanded] = useState(false);
  const [guideCopied, setGuideCopied] = useState(false);
  // Guide modal opened from the agents list (placeholder token, independent of token modal).
  const [guideModalAgent, setGuideModalAgent] = useState<any | null>(null);
  const [listGuideCopied, setListGuideCopied] = useState(false);

  // Workspace settings
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [wsName, setWsName] = useState('');
  const [wsTimezone, setWsTimezone] = useState('');
  const [wsRetention, setWsRetention] = useState(0);
  const [wsSaving, setWsSaving] = useState(false);
  const [wsSaveError, setWsSaveError] = useState<string | null>(null);
  const [wsSaveSuccess, setWsSaveSuccess] = useState(false);

  // Groups state
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#3B82F6');
  const [groupCreating, setGroupCreating] = useState(false);
  const [groupCreateError, setGroupCreateError] = useState<string | null>(null);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupColor, setEditGroupColor] = useState('');
  const [groupUpdating, setGroupUpdating] = useState(false);
  const [groupUpdateError, setGroupUpdateError] = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);

  // Domains state
  const [domains, setDomains] = useState<ImpactDomain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsError, setDomainsError] = useState<string | null>(null);
  const [newDomainName, setNewDomainName] = useState('');
  const [newDomainDescription, setNewDomainDescription] = useState('');
  const [newDomainGroupId, setNewDomainGroupId] = useState('');
  const [domainCreating, setDomainCreating] = useState(false);
  const [domainCreateError, setDomainCreateError] = useState<string | null>(null);
  const [editingDomain, setEditingDomain] = useState<string | null>(null);
  const [editDomainName, setEditDomainName] = useState('');
  const [editDomainDescription, setEditDomainDescription] = useState('');
  const [editDomainGroupId, setEditDomainGroupId] = useState('');
  const [domainUpdating, setDomainUpdating] = useState(false);
  const [domainUpdateError, setDomainUpdateError] = useState<string | null>(null);
  const [deletingDomain, setDeletingDomain] = useState<string | null>(null);

  // Projects state
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectKey, setNewProjectKey] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectMemberIds, setNewProjectMemberIds] = useState<string[]>([]);
  const [newProjectGroupIds, setNewProjectGroupIds] = useState<string[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [projectCreating, setProjectCreating] = useState(false);
  const [projectCreateError, setProjectCreateError] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<string | null>(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectDescription, setEditProjectDescription] = useState('');
  const [projectUpdating, setProjectUpdating] = useState(false);
  const [projectUpdateError, setProjectUpdateError] = useState<string | null>(null);
  const [deletingProject, setDeletingProject] = useState<string | null>(null);

  // Project members (per-project, keyed by project id)
  const [expandedMembersProjectId, setExpandedMembersProjectId] = useState<string | null>(null);
  const [projectMembersMap, setProjectMembersMap] = useState<Record<string, ProjectMember[]>>({});
  const [projectInvitationsMap, setProjectInvitationsMap] = useState<Record<string, ProjectInvitation[]>>({});
  const [projectMembersLoading, setProjectMembersLoading] = useState(false);
  const [projectMembersError, setProjectMembersError] = useState<string | null>(null);
  const [memberInviteEmail, setMemberInviteEmail] = useState('');
  const [memberInviteRole, setMemberInviteRole] = useState<UserRole>('operator');
  const [memberInviteSending, setMemberInviteSending] = useState(false);
  const [memberInviteError, setMemberInviteError] = useState<string | null>(null);
  const [removingProjectMember, setRemovingProjectMember] = useState<string | null>(null);
  const [updatingMemberRoleUserId, setUpdatingMemberRoleUserId] = useState<string | null>(null);
  const [revokingProjectInvitation, setRevokingProjectInvitation] = useState<string | null>(null);

  // Task templates state
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<string | null>(null);

  // Home dashboard widget preferences state
  const [homeWidgetIds, setHomeWidgetIds] = useState<string[]>([]);
  const [homeWidgetsLoading, setHomeWidgetsLoading] = useState(false);
  const [homeWidgetsSaving, setHomeWidgetsSaving] = useState(false);
  const [homeWidgetsError, setHomeWidgetsError] = useState<string | null>(null);
  const [homeWidgetsSaveSuccess, setHomeWidgetsSaveSuccess] = useState(false);

  const timezones = [
    { value: 'Europe/Istanbul', label: 'İstanbul (UTC+3)' },
    { value: 'Europe/London', label: 'Londra (UTC+0)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Europe/Berlin', label: 'Berlin (UTC+1)' },
    { value: 'Europe/Rome', label: 'Roma (UTC+1)' },
    { value: 'Europe/Madrid', label: 'Madrid (UTC+1)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (UTC+1)' },
    { value: 'Europe/Brussels', label: 'Brüksel (UTC+1)' },
    { value: 'Europe/Vienna', label: 'Viyana (UTC+1)' },
    { value: 'Europe/Zurich', label: 'Zürih (UTC+1)' },
    { value: 'Europe/Stockholm', label: 'Stockholm (UTC+1)' },
    { value: 'Europe/Oslo', label: 'Oslo (UTC+1)' },
    { value: 'Europe/Copenhagen', label: 'Kopenhag (UTC+1)' },
    { value: 'Europe/Helsinki', label: 'Helsinki (UTC+2)' },
    { value: 'Europe/Warsaw', label: 'Varşova (UTC+1)' },
    { value: 'Europe/Prague', label: 'Prag (UTC+1)' },
    { value: 'Europe/Budapest', label: 'Budapeşte (UTC+1)' },
    { value: 'Europe/Bucharest', label: 'Bükreş (UTC+2)' },
    { value: 'Europe/Sofia', label: 'Sofya (UTC+2)' },
    { value: 'Europe/Athens', label: 'Atina (UTC+2)' },
    { value: 'Europe/Moscow', label: 'Moskova (UTC+3)' },
    { value: 'Europe/Kiev', label: 'Kiev (UTC+2)' },
    { value: 'America/New_York', label: 'New York (UTC-5)' },
    { value: 'America/Chicago', label: 'Chicago (UTC-6)' },
    { value: 'America/Denver', label: 'Denver (UTC-7)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8)' },
    { value: 'America/Toronto', label: 'Toronto (UTC-5)' },
    { value: 'America/Vancouver', label: 'Vancouver (UTC-8)' },
    { value: 'America/Mexico_City', label: 'Meksiko (UTC-6)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)' },
    { value: 'America/Santiago', label: 'Santiago (UTC-3)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
    { value: 'Asia/Shanghai', label: 'Şangay (UTC+8)' },
    { value: 'Asia/Beijing', label: 'Pekin (UTC+8)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
    { value: 'Asia/Singapore', label: 'Singapur (UTC+8)' },
    { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
    { value: 'Asia/Jakarta', label: 'Cakarta (UTC+7)' },
    { value: 'Asia/Manila', label: 'Manila (UTC+8)' },
    { value: 'Asia/Seoul', label: 'Seul (UTC+9)' },
    { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
    { value: 'Asia/Kolkata', label: 'Kolkata (UTC+5:30)' },
    { value: 'Asia/Karachi', label: 'Karaçi (UTC+5)' },
    { value: 'Asia/Tashkent', label: 'Taşkent (UTC+5)' },
    { value: 'Asia/Almaty', label: 'Almatı (UTC+6)' },
    { value: 'Asia/Bishkek', label: 'Bişkek (UTC+6)' },
    { value: 'Australia/Sydney', label: 'Sydney (UTC+10)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (UTC+10)' },
    { value: 'Australia/Perth', label: 'Perth (UTC+8)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (UTC+9:30)' },
    { value: 'Australia/Darwin', label: 'Darwin (UTC+9:30)' },
    { value: 'Pacific/Auckland', label: 'Auckland (UTC+12)' },
    { value: 'Pacific/Fiji', label: 'Fiji (UTC+12)' },
    { value: 'Africa/Cairo', label: 'Kahire (UTC+2)' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg (UTC+2)' },
    { value: 'Africa/Lagos', label: 'Lagos (UTC+1)' },
    { value: 'Africa/Casablanca', label: 'Kazablanka (UTC+0)' },
    { value: 'Africa/Nairobi', label: 'Nairobi (UTC+3)' },
    { value: 'Africa/Algiers', label: 'Cezayir (UTC+1)' },
    { value: 'Africa/Tunis', label: 'Tunus (UTC+1)' },
    { value: 'Africa/Rabat', label: 'Rabat (UTC+0)' },
  ];

  const colorOptions = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'
  ];

  const roleOptions: { value: UserRole; label: string }[] = [
    { value: 'superadmin', label: 'Superadmin' },
    { value: 'manager', label: 'Manager' },
    { value: 'operator', label: 'Operator' },
    { value: 'client_user', label: 'Client User' },
  ];

  // ---- Data Fetching ----

  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const res = await api.get('/workspace/groups');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setGroups(data);
    } catch (err: any) {
      setGroupsError(getApiErrorMessage(err, 'Failed to load groups.'));
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await api.get('/workspace/members');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setMembers(data);
    } catch (err) {
      console.error('Failed to load workspace members:', err);
    }
  }, []);

  const fetchDomains = useCallback(async () => {
    setDomainsLoading(true);
    setDomainsError(null);
    try {
      const res = await api.get('/workspace/domains');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setDomains(data);
    } catch (err: any) {
      setDomainsError(getApiErrorMessage(err, 'Failed to load domains.'));
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const res = await api.get('/projects');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setProjects(data);
    } catch (err: any) {
      setProjectsError(getApiErrorMessage(err, 'Failed to load projects.'));
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const res = await api.get('/tasks/templates');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setTemplates(data);
    } catch (err: any) {
      setTemplatesError(getApiErrorMessage(err, 'Failed to load templates.'));
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const fetchHomeWidgetPreferences = useCallback(async () => {
    setHomeWidgetsLoading(true);
    setHomeWidgetsError(null);
    try {
      const res = await api.get('/dashboard/widget-preferences');
      const data = (res.data as any)?.data;
      if (Array.isArray(data?.widget_ids)) setHomeWidgetIds(data.widget_ids);
    } catch (err: any) {
      setHomeWidgetsError(getApiErrorMessage(err, 'Failed to load widget preferences.'));
    } finally {
      setHomeWidgetsLoading(false);
    }
  }, []);

  const toggleHomeWidget = (id: string) => {
    setHomeWidgetsSaveSuccess(false);
    setHomeWidgetIds((prev) => {
      if (prev.includes(id)) return prev.filter((w) => w !== id);
      if (prev.length >= MAX_DASHBOARD_WIDGETS) return prev;
      return [...prev, id];
    });
  };

  const handleSaveHomeWidgets = async () => {
    setHomeWidgetsSaving(true);
    setHomeWidgetsError(null);
    setHomeWidgetsSaveSuccess(false);
    try {
      await api.put('/dashboard/widget-preferences', { widget_ids: homeWidgetIds });
      setHomeWidgetsSaveSuccess(true);
    } catch (err: any) {
      setHomeWidgetsError(getApiErrorMessage(err, 'Failed to save widget preferences.'));
    } finally {
      setHomeWidgetsSaving(false);
    }
  };

  const fetchInvitations = useCallback(async () => {
    if (!canManage) return;
    setInvitationsLoading(true);
    setInvitationsError(null);
    try {
      const res = await api.get('/workspace/invitations');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setInvitations(data);
    } catch (err: any) {
      setInvitationsError(getApiErrorMessage(err, 'Failed to load invitations.'));
    } finally {
      setInvitationsLoading(false);
    }
  }, [canManage]);

  const fetchAgents = useCallback(async () => {
    if (!canManage) return;
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      const res = await api.get('/workspace/agents');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setAgents(data);
    } catch (err: any) {
      setAgentsError(getApiErrorMessage(err, 'Failed to load agents.'));
    } finally {
      setAgentsLoading(false);
    }
  }, [canManage]);

  const fetchWorkspace = useCallback(async () => {
    // Reuse the shared store's workspace if Header.tsx already fetched it
    // — only fall back to a fresh API call when it hasn't (e.g. this page
    // loaded before that fetch resolved). Read via getState() rather than
    // subscribing to the value directly, so this callback's own identity
    // stays stable and doesn't re-trigger the combined fetch effect below
    // every time the shared workspace changes.
    const existing = useWorkspaceStore.getState().workspace;
    if (existing) {
      setWorkspace(existing);
      setWsName(existing.name);
      setWsTimezone(existing.timezone);
      setWsRetention(existing.incident_retention_count);
      return;
    }
    setWorkspaceLoading(true);
    setWorkspaceError(null);
    try {
      const res = await api.get('/workspace/me');
      const data = (res.data as any)?.data as Workspace;
      if (data) {
        setWorkspace(data);
        setSharedWorkspace(data);
        setWsName(data.name);
        setWsTimezone(data.timezone);
        setWsRetention(data.incident_retention_count);
      }
    } catch (err: any) {
      setWorkspaceError(getApiErrorMessage(err, 'Failed to load workspace.'));
    } finally {
      setWorkspaceLoading(false);
    }
  }, [setSharedWorkspace]);

  useEffect(() => {
    fetchGroups();
    fetchDomains();
    fetchProjects();
    fetchInvitations();
    fetchAgents();
    fetchWorkspace();
    fetchMembers();
    fetchTemplates();
    fetchHomeWidgetPreferences();
  }, [fetchGroups, fetchDomains, fetchProjects, fetchInvitations, fetchAgents, fetchWorkspace, fetchMembers, fetchTemplates, fetchHomeWidgetPreferences]);

  // ---- Invitation Handlers ----

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviteSending(true);
    setInviteError(null);
    try {
      const payload: Record<string, any> = { email: inviteEmail, role: inviteRole };
      if (inviteGroupId) payload.group_id = inviteGroupId;
      await api.post('/workspace/invitations', payload);
      setInviteEmail('');
      setInviteRole('operator');
      setInviteGroupId('');
      await fetchInvitations();
    } catch (err: any) {
      setInviteError(getApiErrorMessage(err, 'Failed to send invitation.'));
    } finally {
      setInviteSending(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!(await confirm('Bu daveti iptal etmek istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setRevokingInvitation(invitationId);
    try {
      await api.delete(`/workspace/invitations/${invitationId}`);
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to revoke invitation.'));
    } finally {
      setRevokingInvitation(null);
    }
  };

  // ---- Agents ----

  const copyToken = async () => {
    if (!agentPlaintextToken) return;
    try {
      await navigator.clipboard.writeText(agentPlaintextToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard access denied — silently ignore
    }
  };

  const copyGuide = async () => {
    if (!agentPlaintextToken || !createdAgentInfo) return;
    try {
      await navigator.clipboard.writeText(
        buildAgentGuide({
          agentName: createdAgentInfo.name,
          agentId: createdAgentInfo.id,
          workspaceName: workspace?.name ?? '',
          groupNames: createdAgentInfo.groupNames,
          scopes: createdAgentInfo.scopes,
          token: agentPlaintextToken,
          t: t as (key: string) => string,
        })
      );
      setGuideCopied(true);
      setTimeout(() => setGuideCopied(false), 2000);
    } catch {
      // clipboard access denied — silently ignore
    }
  };

  const copyListGuide = async () => {
    if (!guideModalAgent) return;
    try {
      await navigator.clipboard.writeText(
        buildAgentGuide({
          agentName: guideModalAgent.name,
          agentId: guideModalAgent.id,
          workspaceName: workspace?.name ?? '',
          groupNames: (guideModalAgent.group_ids || []).map((gid: string) => groups.find((g) => g.id === gid)?.name ?? gid),
          scopes: guideModalAgent.scopes && guideModalAgent.scopes.length > 0 ? guideModalAgent.scopes : ['-'],
          token: '<YOUR_TOKEN_HERE>',
          t: t as (key: string) => string,
        })
      );
      setListGuideCopied(true);
      setTimeout(() => setListGuideCopied(false), 2000);
    } catch {
      // clipboard access denied — silently ignore
    }
  };

  const closeTokenModal = () => {
    setShowTokenModal(false);
    setAgentPlaintextToken(null);
    setCreatedAgentInfo(null);
    setGuideExpanded(false);
    setGuideCopied(false);
  };

  const closeGuideModal = () => {
    setGuideModalAgent(null);
    setListGuideCopied(false);
  };

  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = agentName.trim();
    if (trimmed.length < 2) {
      setAgentError('Name must be at least 2 characters.');
      return;
    }
    if (selectedGroupIds.length === 0) {
      setAgentError('Please select at least one group.');
      return;
    }
    if (selectedScopes.length === 0) {
      setAgentError('Please select at least one permission scope.');
      return;
    }
    setAgentCreating(true);
    setAgentError(null);
    try {
      const res = await api.post('/workspace/agents', {
        name: trimmed,
        group_ids: selectedGroupIds,
        scopes: selectedScopes,
        role: agentRole,
      });
      const data = (res.data as any)?.data;
      if (data?.plaintext) {
        setAgentPlaintextToken(data.plaintext);
        setCreatedAgentInfo({
          id: data.agent?.id ?? '',
          name: data.agent?.name ?? trimmed,
          groupNames: selectedGroupIds.map((gid) => groups.find((g) => g.id === gid)?.name ?? gid),
          scopes: [...selectedScopes],
        });
        setGuideExpanded(false);
        setGuideCopied(false);
        setShowTokenModal(true);
      }
      setAgentName('');
      setSelectedGroupIds([]);
      setSelectedScopes([]);
      setAgentRole('operator');
      await fetchAgents();
    } catch (err: any) {
      setAgentError(getApiErrorMessage(err, 'Failed to create agent.'));
    } finally {
      setAgentCreating(false);
    }
  };

  const handleDeactivateAgent = async (agentId: string) => {
    if (!(await confirm(t('settings.agents.deactivateConfirm'), { variant: 'danger' }))) return;
    setDeactivatingAgent(agentId);
    try {
      await api.delete(`/workspace/agents/${agentId}`);
      setAgents((prev) => prev.filter((a) => a.id !== agentId));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to deactivate agent.'));
    } finally {
      setDeactivatingAgent(null);
    }
  };

  // Rotates the agent's token: the old one stops working the moment this
  // succeeds, so the new plaintext is shown via the same one-time modal
  // the create flow uses (createdAgentInfo feeds its Guide section too).
  const handleRegenerateToken = async (agent: any) => {
    if (!(await confirm(t('settings.agents.regenerateConfirm'), { variant: 'danger' }))) return;
    setRegeneratingAgent(agent.id);
    try {
      const res = await api.post(`/workspace/agents/${agent.id}/tokens/regenerate`);
      const data = (res.data as any)?.data;
      if (data?.plaintext) {
        setAgentPlaintextToken(data.plaintext);
        setShowTokenModal(true);
        setCreatedAgentInfo({
          id: agent.id,
          name: agent.name,
          groupNames: (agent.group_ids || []).map((gid: string) => groups.find((g) => g.id === gid)?.name ?? gid),
          scopes: data.token?.scopes ?? agent.scopes ?? []
        });
      }
      await fetchAgents();
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to regenerate token.'));
    } finally {
      setRegeneratingAgent(null);
    }
  };

  // "Terminate": revokes every active token without deactivating the
  // agent account itself — distinct from Deactivate (see agent.service.ts).
  const handleTerminateTokens = async (agentId: string) => {
    if (!(await confirm(t('settings.agents.terminateConfirm'), { variant: 'danger' }))) return;
    setTerminatingAgent(agentId);
    try {
      await api.post(`/workspace/agents/${agentId}/tokens/revoke-all`);
      await fetchAgents();
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to terminate token.'));
    } finally {
      setTerminatingAgent(null);
    }
  };

  // Issues a brand-new, additionally-scoped token on an existing agent —
  // e.g. to grant documents:* to an agent created before that scope
  // existed, without losing its id/history by recreating it.
  const handleIssueToken = async (agent: any) => {
    if (issueTokenScopes.length === 0) return;
    setIssuingToken(true);
    try {
      // Joi caps the token name at 100 chars — joining every selected
      // scope into the name can exceed that once documents:* exists
      // alongside tasks:*/incidents:*, so keep it short and fixed-length
      // instead (the scopes themselves are shown separately, not lost).
      const res = await api.post(`/workspace/agents/${agent.id}/tokens`, {
        name: `${agent.name} — new token (${issueTokenScopes.length} scope${issueTokenScopes.length === 1 ? '' : 's'})`.slice(0, 100),
        scopes: issueTokenScopes
      });
      const data = (res.data as any)?.data;
      if (data?.plaintext) {
        setAgentPlaintextToken(data.plaintext);
        setShowTokenModal(true);
        setCreatedAgentInfo({
          id: agent.id,
          name: agent.name,
          groupNames: (agent.group_ids || []).map((gid: string) => groups.find((g) => g.id === gid)?.name ?? gid),
          scopes: data.token?.scopes ?? issueTokenScopes
        });
      }
      setIssuingTokenAgent(null);
      setIssueTokenScopes([]);
      await fetchAgents();
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to issue token.'));
    } finally {
      setIssuingToken(false);
    }
  };

  const fetchAgentWebhooks = async (agentId: string) => {
    setWebhooksLoading(true);
    try {
      const res = await api.get(`/workspace/agents/${agentId}/webhooks`);
      setAgentWebhooks((prev) => ({ ...prev, [agentId]: (res.data as any)?.data || [] }));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to load webhooks.'));
    } finally {
      setWebhooksLoading(false);
    }
  };

  const toggleWebhooksPanel = (agentId: string) => {
    const opening = webhooksAgent !== agentId;
    setWebhooksAgent(opening ? agentId : null);
    setNewWebhookUrl('');
    setNewWebhookEvents([]);
    setWebhookError(null);
    setCreatedWebhookSecret(null);
    if (opening) fetchAgentWebhooks(agentId);
  };

  const handleCreateWebhook = async (agent: any) => {
    if (!newWebhookUrl || newWebhookEvents.length === 0) return;
    setCreatingWebhook(true);
    setWebhookError(null);
    try {
      const res = await api.post(`/workspace/agents/${agent.id}/webhooks`, { url: newWebhookUrl, events: newWebhookEvents });
      const secret = (res.data as any)?.data?.secret;
      if (secret) setCreatedWebhookSecret(secret);
      setNewWebhookUrl('');
      setNewWebhookEvents([]);
      await fetchAgentWebhooks(agent.id);
    } catch (err: any) {
      setWebhookError(getApiErrorMessage(err, 'Failed to create webhook.'));
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (agent: any, webhookId: string) => {
    if (!(await confirm(t('settings.agents.webhooks.deleteConfirm'), { variant: 'danger' }))) return;
    setDeletingWebhookId(webhookId);
    try {
      await api.delete(`/workspace/agents/${agent.id}/webhooks/${webhookId}`);
      await fetchAgentWebhooks(agent.id);
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to delete webhook.'));
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const toggleDeliveries = async (agent: any, webhookId: string) => {
    if (expandedDeliveriesWebhook === webhookId) {
      setExpandedDeliveriesWebhook(null);
      return;
    }
    setExpandedDeliveriesWebhook(webhookId);
    setDeliveriesLoading(true);
    try {
      const res = await api.get(`/workspace/agents/${agent.id}/webhooks/${webhookId}/deliveries`);
      setWebhookDeliveries((prev) => ({ ...prev, [webhookId]: (res.data as any)?.data || [] }));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to load deliveries.'));
    } finally {
      setDeliveriesLoading(false);
    }
  };

  // ---- Workspace Settings Handlers ----

  const handleSaveWorkspace = async () => {
    setWsSaving(true);
    setWsSaveError(null);
    setWsSaveSuccess(false);
    try {
      await api.put('/workspace/settings', {
        name: wsName,
        timezone: wsTimezone
      });
      setWsSaveSuccess(true);
      // Keep the shared store (and this page's own local copy) in sync so
      // Sidebar's workspace name doesn't go stale until a reload.
      const sharedNow = useWorkspaceStore.getState().workspace;
      setWorkspace((prev) => (prev ? { ...prev, name: wsName, timezone: wsTimezone } : prev));
      if (sharedNow) {
        setSharedWorkspace({ ...sharedNow, name: wsName, timezone: wsTimezone });
      }
      setTimeout(() => setWsSaveSuccess(false), 3000);
    } catch (err: any) {
      setWsSaveError(getApiErrorMessage(err, 'Failed to save workspace settings.'));
    } finally {
      setWsSaving(false);
    }
  };

  // ---- Group Handlers ----

  const handleAddGroup = async () => {
    if (!newGroupName) return;
    setGroupCreating(true);
    setGroupCreateError(null);
    try {
      const payload: Record<string, any> = { name: newGroupName };
      if (newGroupColor) payload.color = newGroupColor;
      const res = await api.post('/workspace/groups', payload);
      const created = (res.data as any)?.data as WorkspaceGroup;
      if (created) {
        setGroups((prev) => [...prev, created]);
      } else {
        await fetchGroups();
      }
      setNewGroupName('');
      setNewGroupColor('#3B82F6');
    } catch (err: any) {
      setGroupCreateError(getApiErrorMessage(err, 'Failed to create group.'));
    } finally {
      setGroupCreating(false);
    }
  };

  const handleRemoveGroup = async (groupId: string) => {
    if (!(await confirm('Bu grubu silmek istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setDeletingGroup(groupId);
    try {
      await api.delete(`/workspace/groups/${groupId}`);
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to delete group.'));
    } finally {
      setDeletingGroup(null);
    }
  };

  const startEditGroup = (group: WorkspaceGroup) => {
    setEditingGroup(group.id);
    setEditGroupName(group.name);
    setEditGroupColor(group.color || '');
    setGroupUpdateError(null);
  };

  const cancelEditGroup = () => {
    setEditingGroup(null);
    setEditGroupName('');
    setEditGroupColor('');
    setGroupUpdateError(null);
  };

  const handleUpdateGroup = async (groupId: string) => {
    if (!editGroupName) return;
    setGroupUpdating(true);
    setGroupUpdateError(null);
    try {
      const payload: Record<string, any> = { name: editGroupName };
      if (editGroupColor) payload.color = editGroupColor;
      const res = await api.put(`/workspace/groups/${groupId}`, payload);
      const updated = (res.data as any)?.data as WorkspaceGroup;
      if (updated) {
        setGroups((prev) => prev.map((g) => (g.id === groupId ? updated : g)));
      } else {
        await fetchGroups();
      }
      cancelEditGroup();
    } catch (err: any) {
      setGroupUpdateError(getApiErrorMessage(err, 'Failed to update group.'));
    } finally {
      setGroupUpdating(false);
    }
  };

  // ---- Domain Handlers ----

  const handleAddDomain = async () => {
    if (!newDomainName) return;
    setDomainCreating(true);
    setDomainCreateError(null);
    try {
      const res = await api.post('/workspace/domains', {
        name: newDomainName,
        description: newDomainDescription || null,
        default_group_id: newDomainGroupId || null
      });
      const created = (res.data as any)?.data as ImpactDomain;
      if (created) {
        setDomains((prev) => [...prev, created]);
      } else {
        await fetchDomains();
      }
      setNewDomainName('');
      setNewDomainDescription('');
      setNewDomainGroupId('');
    } catch (err: any) {
      setDomainCreateError(getApiErrorMessage(err, 'Failed to create domain.'));
    } finally {
      setDomainCreating(false);
    }
  };

  const handleRemoveDomain = async (domainId: string) => {
    if (!(await confirm('Bu etki alanını silmek istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setDeletingDomain(domainId);
    try {
      await api.delete(`/workspace/domains/${domainId}`);
      setDomains((prev) => prev.filter((d) => d.id !== domainId));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to delete domain.'));
    } finally {
      setDeletingDomain(null);
    }
  };

  const startEditDomain = (domain: ImpactDomain) => {
    setEditingDomain(domain.id);
    setEditDomainName(domain.name);
    setEditDomainDescription(domain.description || '');
    setEditDomainGroupId(domain.default_group_id || '');
    setDomainUpdateError(null);
  };

  const cancelEditDomain = () => {
    setEditingDomain(null);
    setEditDomainName('');
    setEditDomainDescription('');
    setEditDomainGroupId('');
    setDomainUpdateError(null);
  };

  const handleUpdateDomain = async (domainId: string) => {
    if (!editDomainName) return;
    setDomainUpdating(true);
    setDomainUpdateError(null);
    try {
      const res = await api.put(`/workspace/domains/${domainId}`, {
        name: editDomainName,
        description: editDomainDescription || null,
        default_group_id: editDomainGroupId || null
      });
      const updated = (res.data as any)?.data as ImpactDomain;
      if (updated) {
        setDomains((prev) => prev.map((d) => (d.id === domainId ? updated : d)));
      } else {
        await fetchDomains();
      }
      cancelEditDomain();
    } catch (err: any) {
      setDomainUpdateError(getApiErrorMessage(err, 'Failed to update domain.'));
    } finally {
      setDomainUpdating(false);
    }
  };

  // ---- Project Handlers ----

  const handleAddProject = async () => {
    if (!newProjectName || !newProjectKey) return;
    setProjectCreating(true);
    setProjectCreateError(null);
    try {
      const payload: Record<string, any> = { name: newProjectName, project_key: newProjectKey };
      if (newProjectDescription) payload.description = newProjectDescription;
      if (newProjectMemberIds.length > 0) payload.member_user_ids = newProjectMemberIds;
      if (newProjectGroupIds.length > 0) payload.group_ids = newProjectGroupIds;
      const res = await api.post('/projects', payload);
      const created = (res.data as any)?.data as Project;
      if (created) {
        setProjects((prev) => [...prev, created]);
      } else {
        await fetchProjects();
      }
      setNewProjectName('');
      setNewProjectKey('');
      setNewProjectDescription('');
      setNewProjectMemberIds([]);
      setNewProjectGroupIds([]);
    } catch (err: any) {
      setProjectCreateError(getApiErrorMessage(err, 'Failed to create project.'));
    } finally {
      setProjectCreating(false);
    }
  };

  const handleRemoveProject = async (projectId: string) => {
    if (!(await confirm('Bu projeyi silmek istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setDeletingProject(projectId);
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err: any) {
      alert(getApiErrorMessage(err, 'Failed to delete project.'));
    } finally {
      setDeletingProject(null);
    }
  };

  const startEditProject = (project: Project) => {
    setEditingProject(project.id);
    setEditProjectName(project.name);
    setEditProjectDescription(project.description || '');
    setProjectUpdateError(null);
  };

  const cancelEditProject = () => {
    setEditingProject(null);
    setEditProjectName('');
    setEditProjectDescription('');
    setProjectUpdateError(null);
  };

  const handleUpdateProject = async (projectId: string) => {
    if (!editProjectName) return;
    setProjectUpdating(true);
    setProjectUpdateError(null);
    try {
      const payload: Record<string, any> = { name: editProjectName };
      if (editProjectDescription) payload.description = editProjectDescription;
      const res = await api.put(`/projects/${projectId}`, payload);
      const updated = (res.data as any)?.data as Project;
      if (updated) {
        setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
      } else {
        await fetchProjects();
      }
      cancelEditProject();
    } catch (err: any) {
      setProjectUpdateError(getApiErrorMessage(err, 'Failed to update project.'));
    } finally {
      setProjectUpdating(false);
    }
  };

  const toggleProjectMembers = async (projectId: string) => {
    if (expandedMembersProjectId === projectId) {
      setExpandedMembersProjectId(null);
      return;
    }
    setExpandedMembersProjectId(projectId);
    setMemberInviteEmail('');
    setMemberInviteError(null);
    setProjectMembersError(null);
    setProjectMembersLoading(true);
    try {
      const [membersRes, invitationsRes] = await Promise.all([
        api.get(`/projects/${projectId}/members`),
        api.get(`/projects/${projectId}/invitations`)
      ]);
      const members = (membersRes.data as any)?.data;
      const invitations = (invitationsRes.data as any)?.data;
      setProjectMembersMap((prev) => ({ ...prev, [projectId]: Array.isArray(members) ? members : [] }));
      setProjectInvitationsMap((prev) => ({ ...prev, [projectId]: Array.isArray(invitations) ? invitations : [] }));
    } catch (err: any) {
      setProjectMembersError(getApiErrorMessage(err, 'Failed to load project members.'));
    } finally {
      setProjectMembersLoading(false);
    }
  };

  const handleInviteToProject = async (projectId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!memberInviteEmail) return;
    setMemberInviteSending(true);
    setMemberInviteError(null);
    try {
      const res = await api.post(`/projects/${projectId}/invitations`, {
        email: memberInviteEmail,
        workspace_role: memberInviteRole
      });
      const created = (res.data as any)?.data as ProjectInvitation;
      if (created) {
        setProjectInvitationsMap((prev) => ({
          ...prev,
          [projectId]: [created, ...(prev[projectId] || [])]
        }));
      }
      setMemberInviteEmail('');
    } catch (err: any) {
      setMemberInviteError(getApiErrorMessage(err, 'Failed to send project invitation.'));
    } finally {
      setMemberInviteSending(false);
    }
  };

  const handleRemoveProjectMember = async (projectId: string, userId: string) => {
    if (!(await confirm('Bu üyeyi projeden kaldırmak istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setRemovingProjectMember(userId);
    try {
      await api.delete(`/projects/${projectId}/members/${userId}`);
      setProjectMembersMap((prev) => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter((m) => m.user_id !== userId)
      }));
    } catch (err: any) {
      setProjectMembersError(getApiErrorMessage(err, 'Failed to remove project member.'));
    } finally {
      setRemovingProjectMember(null);
    }
  };

  const handleSetProjectMemberRole = async (projectId: string, userId: string, role: 'manager' | 'member') => {
    setUpdatingMemberRoleUserId(userId);
    try {
      await api.put(`/projects/${projectId}/members/${userId}/role`, { role_in_project: role });
      setProjectMembersMap((prev) => ({
        ...prev,
        [projectId]: (prev[projectId] || []).map((m) => (m.user_id === userId ? { ...m, role_in_project: role } : m))
      }));
    } catch (err: any) {
      setProjectMembersError(getApiErrorMessage(err, 'Failed to update member role.'));
    } finally {
      setUpdatingMemberRoleUserId(null);
    }
  };

  const handleRevokeProjectInvitation = async (projectId: string, invitationId: string) => {
    setRevokingProjectInvitation(invitationId);
    try {
      await api.delete(`/projects/${projectId}/invitations/${invitationId}`);
      setProjectInvitationsMap((prev) => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter((inv) => inv.id !== invitationId)
      }));
    } catch (err: any) {
      setProjectMembersError(getApiErrorMessage(err, 'Failed to revoke project invitation.'));
    } finally {
      setRevokingProjectInvitation(null);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!(await confirm('Bu şablonu silmek istediğinize emin misiniz?', { variant: 'danger' }))) return;
    setDeletingTemplate(templateId);
    try {
      await api.delete(`/tasks/templates/${templateId}`);
      setTemplates((prev) => prev.filter((tpl) => tpl.id !== templateId));
    } catch (err: any) {
      setTemplatesError(getApiErrorMessage(err, 'Failed to delete template.'));
    } finally {
      setDeletingTemplate(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-inset dark:ring-amber-500/30', label: t('settings.userInvitation.status.pending') },
      accepted: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: t('settings.userInvitation.status.accepted') },
      expired: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: t('settings.userInvitation.status.expired') },
      revoked: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: t('settings.userInvitation.status.revoked') }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: status };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.label}
      </span>
    );
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const settingsNavItems: { id: typeof activeSection; label: string }[] = [
    { id: 'profile', label: t('settings.nav.profile') },
    { id: 'preferences', label: t('settings.nav.preferences') },
    { id: 'templates', label: t('settings.nav.templates') },
    { id: 'homeCustomization', label: t('settings.nav.homeCustomization') },
    // Visible to everyone (was the standalone /team page) — group/role
    // management actions inside stay gated to canManage, same as before.
    { id: 'team', label: t('settings.nav.team') },
    // Static reference, visible to everyone — doesn't require an agent/
    // token to exist yet, unlike the per-agent "Guide" (buildAgentGuide).
    { id: 'apiDocs' as const, label: t('settings.nav.apiDocs') },
    ...(canManage ? [
      { id: 'invitations' as const, label: t('settings.nav.invitations') },
      { id: 'domains' as const, label: t('settings.nav.domains') },
      { id: 'projects' as const, label: t('settings.nav.projects') }
    ] : [])
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('settings.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('settings.subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:items-start">
        {/* Settings sub-nav */}
        <aside className="w-full md:w-56 flex-shrink-0 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-2 flex md:block gap-1 overflow-x-auto md:overflow-visible md:space-y-0.5">
          {settingsNavItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSectionChange(item.id)}
              className={`md:w-full whitespace-nowrap flex-shrink-0 md:flex-shrink text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === item.id
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              {item.label}
            </button>
          ))}
        </aside>

        {/* Section content */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* My Profile */}
          {activeSection === 'profile' && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-300 text-2xl font-bold flex-shrink-0">
                    {user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{user?.name || '-'}</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('settings.profile.fields.role')}</span>
                    <p className="text-gray-900 dark:text-gray-100">{user?.role ? t(`settings.profile.roles.${PROFILE_ROLE_KEYS[user.role] || user.role}`) : '-'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('settings.profile.fields.status')}</span>
                    <p className="text-gray-900 dark:text-gray-100">{user?.is_active ? t('settings.profile.fields.active') : t('settings.profile.fields.inactive')}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('settings.profile.fields.memberSince')}</span>
                    <p className="text-gray-900 dark:text-gray-100">{formatProfileDate(user?.created_at)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('settings.profile.fields.lastLogin')}</span>
                    <p className="text-gray-900 dark:text-gray-100">{formatProfileDate(user?.last_login)}</p>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('settings.profile.details.title')}</h3>

                  {profileSaveError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{profileSaveError}</div>
                  )}
                  {profileSaveSuccess && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg text-sm mb-4">{t('settings.profile.details.saveSuccess')}</div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.details.fullName')}</label>
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.details.jobTitle')}</label>
                      <input
                        type="text"
                        value={profileJobTitle}
                        onChange={(e) => setProfileJobTitle(e.target.value)}
                        placeholder={t('settings.profile.details.jobTitlePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.details.phone')}</label>
                      <input
                        type="tel"
                        value={profilePhone}
                        onChange={(e) => setProfilePhone(e.target.value)}
                        placeholder={t('settings.profile.details.phonePlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.details.timezone')}</label>
                      <select
                        value={profileTimezone}
                        onChange={(e) => setProfileTimezone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">{t('settings.profile.details.timezoneDefault')}</option>
                        {timezones.map((tz) => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={handleSaveProfileDetails}
                    disabled={profileSaving || !profileName}
                    className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {profileSaving ? t('settings.profile.details.saving') : t('settings.profile.details.saveButton')}
                  </button>
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('settings.profile.changePassword.title')}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('settings.profile.changePassword.description')}
                </p>

                {pwChangeMessage && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg text-sm mb-4">{pwChangeMessage}</div>
                )}

                {pwChangeError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{pwChangeError}</div>
                )}

                <div className="space-y-3 max-w-sm">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.changePassword.currentPassword')}</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.changePassword.newPassword')}</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.profile.changePassword.confirmNewPassword')}</label>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  onClick={handleChangePassword}
                  disabled={pwChangeLoading || !currentPassword || !newPassword || !confirmNewPassword}
                  className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pwChangeLoading ? t('settings.profile.changePassword.saving') : t('settings.profile.changePassword.saveButton')}
                </button>
              </div>
            </div>
          )}

          {/* User Invitation */}
          {activeSection === 'invitations' && canManage && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
              <div className="flex items-center space-x-2 mb-3">
                <UserPlus className="h-5 w-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.userInvitation.title')}</h2>
              </div>

              {inviteError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{inviteError}</div>
              )}

              <form onSubmit={handleSendInvitation} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.userInvitation.email')} *</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('settings.userInvitation.emailPlaceholder') as string}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.userInvitation.role')} *</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {roleOptions.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.userInvitation.group')}</label>
                  <select
                    value={inviteGroupId}
                    onChange={(e) => setInviteGroupId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">{t('settings.userInvitation.selectGroup')}</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={inviteSending || !inviteEmail}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {inviteSending ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                  <span>{t('settings.userInvitation.sendInvite')}</span>
                </button>
              </form>

              {/* Pending Invitations */}
              <div className="mt-4">
                <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('settings.userInvitation.pendingInvitations')}</h3>

                {invitationsError && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{invitationsError}</div>
                )}

                {invitationsLoading && invitations.length === 0 && (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('common.loading')}</div>
                )}

                {!invitationsLoading && invitations.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{t('settings.userInvitation.noInvitations')}</p>
                )}

                {invitations.length > 0 && (
                  <div className="space-y-2">
                    {invitations.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{inv.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{inv.role}</span>
                            {getStatusBadge(inv.status)}
                            <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(inv.created_at)}</span>
                          </div>
                        </div>
                        {inv.status === 'pending' && (
                          <button
                            onClick={() => handleRevokeInvitation(inv.id)}
                            disabled={revokingInvitation === inv.id}
                            className="ml-2 p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                            title={t('settings.userInvitation.revoke') as string}
                          >
                            {revokingInvitation === inv.id ? (
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Agent Token Modal */}
          {showTokenModal && agentPlaintextToken && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={closeTokenModal} />
              <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.agents.tokenShownOnce')}</h2>
                </div>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('settings.agents.copyToken')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={agentPlaintextToken}
                      readOnly
                      className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                      onClick={copyToken}
                      disabled={copied}
                      className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:opacity-70 transition-colors text-sm font-medium"
                    >
                      {copied ? t('settings.agents.copied') : t('settings.agents.copyToken')}
                    </button>
                  </div>
                </div>
                {createdAgentInfo && (
                  <div className="mb-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setGuideExpanded(!guideExpanded)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100"
                    >
                      {t('settings.agents.guide.viewGuide')}
                      <span>{guideExpanded ? '−' : '+'}</span>
                    </button>
                    {guideExpanded && (
                      <div className="px-3 pb-3 space-y-2">
                        <textarea
                          readOnly
                          rows={18}
                          value={buildAgentGuide({
                            agentName: createdAgentInfo.name,
                            agentId: createdAgentInfo.id,
                            workspaceName: workspace?.name ?? '',
                            groupNames: createdAgentInfo.groupNames,
                            scopes: createdAgentInfo.scopes,
                            token: agentPlaintextToken,
                            t: t as (key: string) => string,
                          })}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 outline-none"
                        />
                        <button
                          type="button"
                          onClick={copyGuide}
                          disabled={guideCopied}
                          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:opacity-70 transition-colors text-sm font-medium"
                        >
                          {guideCopied ? t('settings.agents.guide.guideCopied') : t('settings.agents.guide.copyGuide')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={closeTokenModal}
                  className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-300 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          )}

          {/* Agent Guide Modal (from agents list, placeholder token) */}
          {activeSection === 'team' && canManage && guideModalAgent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4" onClick={closeGuideModal}>
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-5 max-h-[90vh] overflow-y-auto"
              >
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t('settings.agents.guide.title')} — {guideModalAgent.name}
                </h2>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-3 py-2 rounded-lg text-sm mb-3">
                  {t('settings.agents.guide.guideTokenPlaceholderNote')}
                </div>
                <textarea
                  readOnly
                  rows={20}
                  value={buildAgentGuide({
                    agentName: guideModalAgent.name,
                    agentId: guideModalAgent.id,
                    workspaceName: workspace?.name ?? '',
                    groupNames: (guideModalAgent.group_ids || []).map((gid: string) => groups.find((g) => g.id === gid)?.name ?? gid),
                    scopes: guideModalAgent.scopes && guideModalAgent.scopes.length > 0 ? guideModalAgent.scopes : ['-'],
                    token: '<YOUR_TOKEN_HERE>',
                    t: t as (key: string) => string,
                  })}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 outline-none mb-3"
                />
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={copyListGuide}
                    disabled={listGuideCopied}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white text-sm transition-colors"
                  >
                    {listGuideCopied ? t('settings.agents.guide.guideCopied') : t('settings.agents.guide.copyGuide')}
                  </button>
                  <button
                    type="button"
                    onClick={closeGuideModal}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Groups Management */}
          {activeSection === 'team' && (
          <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.userGroups.title')}</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{groups.length}/{workspace?.max_groups ?? 5}</span>
            </div>

            {groupsError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{groupsError}</div>
            )}

            {groupsLoading && groups.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">Yükleniyor...</div>
            )}

            <div className="space-y-4 mb-6">
              {groups.map((group) => (
                <div key={group.id} className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  {editingGroup === group.id ? (
                    <div className="space-y-3">
                      {groupUpdateError && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{groupUpdateError}</div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grup Adı</label>
                        <input
                          type="text"
                          value={editGroupName}
                          onChange={(e) => setEditGroupName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Renk</label>
                        <div className="flex space-x-2">
                          {colorOptions.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => setEditGroupColor(color)}
                              className={`w-8 h-8 rounded-full border-2 ${editGroupColor === color ? 'border-gray-400 dark:border-gray-300' : 'border-gray-200 dark:border-gray-600'}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateGroup(group.id)}
                          disabled={groupUpdating || !editGroupName}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {groupUpdating ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button
                          onClick={cancelEditGroup}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: group.color || '#6B7280' }}
                          />
                          <div className="font-medium text-gray-900 dark:text-gray-100">{group.name}</div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => startEditGroup(group)}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                              title="Düzenle"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRemoveGroup(group.id)}
                              disabled={deletingGroup === group.id}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                              title="Sil"
                            >
                              {deletingGroup === group.id ? (
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      {domains.filter((d) => d.default_group_id === group.id).length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 pl-7">
                          İlişkili Domainler: {domains.filter((d) => d.default_group_id === group.id).map((d) => d.name).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canManage && groups.length < (workspace?.max_groups ?? 5) && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('settings.userGroups.createNewGroup')}
                </div>
                {groupCreateError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{groupCreateError}</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.userGroups.groupName')}</label>
                    <input
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('settings.userGroups.groupNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.userGroups.groupColor')}</label>
                    <div className="flex space-x-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewGroupColor(color)}
                          className={`w-8 h-8 rounded-full border-2 ${newGroupColor === color ? 'border-gray-400 dark:border-gray-300' : 'border-gray-200 dark:border-gray-600'}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleAddGroup}
                  disabled={groupCreating || !newGroupName}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {groupCreating ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  <span>{t('settings.userGroups.createGroup')}</span>
                </button>
              </div>
            )}

            {canManage && groups.length >= (workspace?.max_groups ?? 5) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('settings.userGroups.limitReached', { max: workspace?.max_groups ?? 5 })}</p>
            )}
          </div>

          <Team embedded />
          {/* Agents — moved to the bottom of "Kullanıcılar ve Gruplar" and
              redesigned (2026-10-18, user feedback): list first (now with a
              robot avatar + owner column), then a collapsed-by-default
              "+ Add Your Agent" trigger below it instead of an always-open
              form. Explicit canManage check since 'team' (unlike the old
              'invitations' home for this) is visible to every role. */}
          {activeSection === 'team' && canManage && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.agents.agentsListTitle')}</h2>
                </div>
                {agentsLoading ? (
                  <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                ) : agentsError ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg px-3 py-2 text-sm">
                    {agentsError}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.name')}</th>
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.owner')}</th>
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.role')}</th>
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.groups')}</th>
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.tokenCount')}</th>
                          <th className="text-left pb-2 text-gray-500 dark:text-gray-400">{t('settings.agents.lastUsed')}</th>
                          <th className="text-right pb-2 text-gray-500 dark:text-gray-400"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {agents.map((agent) => (
                          <React.Fragment key={agent.id}>
                          <tr className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-2 text-gray-900 dark:text-gray-100">
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center p-1"
                                  style={{ backgroundColor: `#${ownerColor(agent.owner_id)}` }}
                                  title={agent.owner_name ? `${t('settings.agents.owner')}: ${agent.owner_name}` : undefined}
                                >
                                  <img src={agentAvatarDataUri(agent.id)} alt="" className="w-full h-full rounded-full" />
                                </span>
                                <span>{agent.name}</span>
                              </div>
                            </td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">{agent.owner_name || '-'}</td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">{agent.role}</td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">
                              {(agent.group_ids || []).map((gid: string) => groups.find((g) => g.id === gid)?.name).join(', ') || '-'}
                            </td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">{agent.token_count ?? 0}</td>
                            <td className="py-2 text-gray-900 dark:text-gray-100">
                              {agent.last_used_at ? formatDate(agent.last_used_at) : t('settings.agents.never')}
                            </td>
                            <td className="py-2 text-right whitespace-nowrap">
                              <button
                                onClick={() => setGuideModalAgent(agent)}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline mr-3"
                              >
                                {t('settings.agents.guide.viewGuide')}
                              </button>
                              <button
                                onClick={() => {
                                  setIssuingTokenAgent(issuingTokenAgent === agent.id ? null : agent.id);
                                  setIssueTokenScopes([]);
                                }}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline mr-3"
                              >
                                {t('settings.agents.newToken')}
                              </button>
                              <button
                                onClick={() => toggleWebhooksPanel(agent.id)}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline mr-3"
                              >
                                {t('settings.agents.webhooks.manage')}
                              </button>
                              <button
                                onClick={() => handleRegenerateToken(agent)}
                                disabled={regeneratingAgent === agent.id}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 mr-3"
                              >
                                {regeneratingAgent === agent.id ? t('settings.agents.regenerating') : t('settings.agents.regenerate')}
                              </button>
                              <button
                                onClick={() => handleTerminateTokens(agent.id)}
                                disabled={terminatingAgent === agent.id}
                                className="text-sm text-amber-600 dark:text-amber-400 hover:underline disabled:opacity-50 mr-3"
                              >
                                {terminatingAgent === agent.id ? t('settings.agents.terminating') : t('settings.agents.terminate')}
                              </button>
                              <button
                                onClick={() => handleDeactivateAgent(agent.id)}
                                disabled={deactivatingAgent === agent.id}
                                className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                              >
                                {deactivatingAgent === agent.id ? t('settings.agents.deactivating') : t('settings.agents.deactivate')}
                              </button>
                            </td>
                          </tr>
                          {issuingTokenAgent === agent.id && (
                            <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                              <td colSpan={7} className="py-3">
                                <div className="max-w-md">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.agents.newTokenScopesPrompt')}</span>
                                    <button
                                      onClick={() => setIssueTokenScopes(['tasks:read', 'tasks:write', 'incidents:read', 'incidents:write', 'documents:read', 'documents:write'])}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      {t('settings.agents.newTokenSelectAll')}
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-950 rounded-lg p-3 mb-3">
                                    {[
                                      { id: 'tasks:read', label: t('settings.agents.scopeTasksRead') },
                                      { id: 'tasks:write', label: t('settings.agents.scopeTasksWrite') },
                                      { id: 'incidents:read', label: t('settings.agents.scopeIncidentsRead') },
                                      { id: 'incidents:write', label: t('settings.agents.scopeIncidentsWrite') },
                                      { id: 'documents:read', label: t('settings.agents.scopeDocumentsRead') },
                                      { id: 'documents:write', label: t('settings.agents.scopeDocumentsWrite') },
                                    ].map((s) => (
                                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer select-none text-gray-900 dark:text-gray-200">
                                        <input
                                          type="checkbox"
                                          checked={issueTokenScopes.includes(s.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) setIssueTokenScopes([...issueTokenScopes, s.id]);
                                            else setIssueTokenScopes(issueTokenScopes.filter((id) => id !== s.id));
                                          }}
                                          className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        {s.label}
                                      </label>
                                    ))}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <button
                                      onClick={() => handleIssueToken(agent)}
                                      disabled={issuingToken || issueTokenScopes.length === 0}
                                      className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      {t('settings.agents.newTokenSubmit')}
                                    </button>
                                    <button
                                      onClick={() => { setIssuingTokenAgent(null); setIssueTokenScopes([]); }}
                                      className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
                                    >
                                      {t('settings.agents.newTokenCancel')}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          {webhooksAgent === agent.id && (
                            <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                              <td colSpan={7} className="py-3">
                                <div className="max-w-xl space-y-3">
                                  {createdWebhookSecret && (
                                    <div className="text-xs bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
                                      <p className="font-medium text-yellow-800 dark:text-yellow-300 mb-1">{t('settings.agents.webhooks.secretShownOnce')}</p>
                                      <code className="block break-all bg-white dark:bg-gray-950 border border-yellow-200 dark:border-yellow-800 rounded px-2 py-1 text-gray-900 dark:text-gray-100">{createdWebhookSecret}</code>
                                    </div>
                                  )}

                                  {webhooksLoading ? (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">…</p>
                                  ) : (agentWebhooks[agent.id] || []).length === 0 ? (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.agents.webhooks.none')}</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {(agentWebhooks[agent.id] || []).map((wh: any) => (
                                        <div key={wh.id} className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                                          <div className="flex items-center justify-between gap-3 text-xs">
                                            <div className="min-w-0">
                                              <p className="truncate text-gray-900 dark:text-gray-100">{wh.url}</p>
                                              <p className="text-gray-500 dark:text-gray-400">
                                                {wh.events.join(', ')} · {wh.last_triggered_at ? formatDate(wh.last_triggered_at) : t('settings.agents.never')}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                              <button
                                                onClick={() => toggleDeliveries(agent, wh.id)}
                                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                              >
                                                {t('settings.agents.webhooks.viewDeliveries')}
                                              </button>
                                              <button
                                                onClick={() => handleDeleteWebhook(agent, wh.id)}
                                                disabled={deletingWebhookId === wh.id}
                                                className="text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                                              >
                                                {t('settings.agents.webhooks.delete')}
                                              </button>
                                            </div>
                                          </div>
                                          {expandedDeliveriesWebhook === wh.id && (
                                            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 space-y-1">
                                              {deliveriesLoading ? (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">…</p>
                                              ) : (webhookDeliveries[wh.id] || []).length === 0 ? (
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.agents.webhooks.noDeliveries')}</p>
                                              ) : (
                                                (webhookDeliveries[wh.id] || []).map((d: any) => (
                                                  <div key={d.id} className="flex items-center gap-2 text-xs">
                                                    <span className={d.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                                      {d.success ? '✓' : '✗'}
                                                    </span>
                                                    <span className="text-gray-700 dark:text-gray-300">{d.event}</span>
                                                    {d.http_status != null && <span className="text-gray-500 dark:text-gray-400">HTTP {d.http_status}</span>}
                                                    {d.error_message && <span className="text-red-600 dark:text-red-400 truncate">{d.error_message}</span>}
                                                    <span className="text-gray-400 dark:text-gray-500 ml-auto flex-shrink-0">{formatDate(d.attempted_at)}</span>
                                                  </div>
                                                ))
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settings.agents.webhooks.addPrompt')}</div>
                                    <input
                                      type="text"
                                      value={newWebhookUrl}
                                      onChange={(e) => setNewWebhookUrl(e.target.value)}
                                      placeholder="https://..."
                                      className="w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-1.5 mb-2 text-gray-900 dark:text-gray-100"
                                    />
                                    <div className="flex flex-wrap gap-3 mb-2">
                                      {['task.note_added', 'task.reassigned', 'incident.note_added', 'incident.reassigned'].map((ev) => (
                                        <label key={ev} className="flex items-center gap-1.5 text-xs cursor-pointer select-none text-gray-900 dark:text-gray-200">
                                          <input
                                            type="checkbox"
                                            checked={newWebhookEvents.includes(ev)}
                                            onChange={(e) => {
                                              if (e.target.checked) setNewWebhookEvents([...newWebhookEvents, ev]);
                                              else setNewWebhookEvents(newWebhookEvents.filter((x) => x !== ev));
                                            }}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                          />
                                          {ev}
                                        </label>
                                      ))}
                                    </div>
                                    {webhookError && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{webhookError}</p>}
                                    <button
                                      onClick={() => handleCreateWebhook(agent)}
                                      disabled={creatingWebhook || !newWebhookUrl || newWebhookEvents.length === 0}
                                      className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      {t('settings.agents.webhooks.add')}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                        {agents.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-2 text-gray-500 dark:text-gray-400 text-center">
                              {t('settings.agents.noAgentsYet')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4">
                <button
                  type="button"
                  onClick={() => setAddAgentExpanded(!addAgentExpanded)}
                  className="w-full flex items-center justify-between text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  <span className="flex items-center gap-2">
                    <Plus className="h-5 w-5 text-blue-600" />
                    {t('settings.agents.addAgentButton')}
                  </span>
                  <span>{addAgentExpanded ? '−' : '+'}</span>
                </button>
                {addAgentExpanded && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('settings.agents.description')}</p>
                    {agentError && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg px-3 py-2 mb-4 text-sm">
                        {agentError}
                      </div>
                    )}
                    <form onSubmit={handleCreateAgent} className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.agents.name')}</label>
                        <input
                          type="text"
                          value={agentName}
                          onChange={(e) => setAgentName(e.target.value)}
                          placeholder={t('settings.agents.namePlaceholder')}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.agents.groups')}</label>
                        <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg p-3 space-y-1 text-sm">
                          {groups.map((g) => (
                            <label key={g.id} className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={selectedGroupIds.includes(g.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedGroupIds([...selectedGroupIds, g.id]);
                                  else setSelectedGroupIds(selectedGroupIds.filter((id) => id !== g.id));
                                }}
                                className="rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-gray-900 dark:text-gray-200">{g.name}</span>
                            </label>
                          ))}
                          {groups.length === 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.agents.noGroupsAvailable')}</p>
                          )}
                        </div>
                      </div>
                      {/* Scopes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.agents.scopes')}</label>
                        <div className="grid grid-cols-2 gap-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg p-3">
                          {[
                            { id: 'tasks:read', label: t('settings.agents.scopeTasksRead') },
                            { id: 'tasks:write', label: t('settings.agents.scopeTasksWrite') },
                            { id: 'incidents:read', label: t('settings.agents.scopeIncidentsRead') },
                            { id: 'incidents:write', label: t('settings.agents.scopeIncidentsWrite') },
                            { id: 'documents:read', label: t('settings.agents.scopeDocumentsRead') },
                            { id: 'documents:write', label: t('settings.agents.scopeDocumentsWrite') },
                          ].map((s) => (
                            <label key={s.id} className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={selectedScopes.includes(s.id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedScopes([...selectedScopes, s.id]);
                                  else setSelectedScopes(selectedScopes.filter((id) => id !== s.id));
                                }}
                                className="rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-gray-900 dark:text-gray-200">{s.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.agents.role')}</label>
                        <select
                          value={agentRole}
                          onChange={(e) => setAgentRole(e.target.value as 'operator' | 'client_user')}
                          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        >
                          <option value="operator">{t('settings.agents.roleOperator')}</option>
                          <option value="client_user">{t('settings.agents.roleClientUser')}</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        disabled={agentCreating}
                        className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-70 transition-colors text-sm font-medium"
                      >
                        {agentCreating ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t('settings.agents.create')}
                          </>
                        ) : (
                          t('settings.agents.create')
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          )}

          </div>
          )}

          {/* Home dashboard widget customization */}
          {activeSection === 'homeCustomization' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.homeCustomization.title')}</h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">{t('settings.homeCustomization.selectedCount', { count: homeWidgetIds.length, max: MAX_DASHBOARD_WIDGETS })}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('settings.homeCustomization.description', { max: MAX_DASHBOARD_WIDGETS })}</p>

            {homeWidgetsError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{homeWidgetsError}</div>
            )}

            {homeWidgetsLoading ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">Yükleniyor...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {DASHBOARD_WIDGETS.map((w) => {
                  const checked = homeWidgetIds.includes(w.id);
                  const disabled = !checked && homeWidgetIds.length >= MAX_DASHBOARD_WIDGETS;
                  return (
                    <label
                      key={w.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                        checked
                          ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleHomeWidget(w.id)}
                        className="rounded"
                      />
                      <span className="text-gray-900 dark:text-gray-100">{t(w.labelKey)}</span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleSaveHomeWidgets}
                disabled={homeWidgetsSaving || homeWidgetsLoading}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {homeWidgetsSaving ? t('settings.homeCustomization.saving') : t('settings.homeCustomization.save')}
              </button>
              {homeWidgetsSaveSuccess && (
                <span className="text-sm text-green-600 dark:text-green-400">{t('settings.homeCustomization.saveSuccess')}</span>
              )}
            </div>
          </div>
          )}

          {/* Preferences (workspace settings + personal notification preference) */}
          {activeSection === 'preferences' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Globe className="h-5 w-5 text-purple-600" />
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.workspace.title')}</h2>
            </div>

            {workspaceError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{workspaceError}</div>
            )}

            {workspaceLoading && !workspace && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('settings.workspace.loading')}</div>
            )}

            {wsSaveSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/40 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg text-sm mb-4">{t('settings.workspace.saveSuccess')}</div>
            )}

            {wsSaveError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{wsSaveError}</div>
            )}

            {workspace && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.workspace.nameLabel')}</label>
                  <input
                    type="text"
                    value={wsName}
                    onChange={(e) => setWsName(e.target.value)}
                    disabled={!isSuperAdmin}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-900/50 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.timezone.title')}</label>
                  <select
                    value={wsTimezone}
                    onChange={(e) => setWsTimezone(e.target.value)}
                    disabled={!isSuperAdmin}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-900/50 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.workspace.retentionLabel')}</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('settings.workspace.retentionHint')}</p>
                  <input
                    type="number"
                    value={wsRetention}
                    readOnly
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed"
                  />
                </div>
                {isSuperAdmin && (
                  <button
                    onClick={handleSaveWorkspace}
                    disabled={wsSaving}
                    className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {wsSaving ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{t('settings.workspace.saveButton')}</span>
                  </button>
                )}
                {!isSuperAdmin && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 text-center">{t('settings.workspace.superadminOnly')}</p>
                )}
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.preferences.notificationLabel')}</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settings.preferences.notificationHint')}</p>
              <select
                value={notificationPreference}
                onChange={(e) => handleSaveNotificationPreference(e.target.value as 'email' | 'in_app')}
                disabled={profileSaving}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="email">{t('settings.preferences.notificationEmail')}</option>
                <option value="in_app">{t('settings.preferences.notificationInApp')}</option>
              </select>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.preferences.reportLanguageLabel')}</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('settings.preferences.reportLanguageHint')}</p>
              <select
                value={reportLanguage}
                onChange={(e) => handleSaveReportLanguage(e.target.value as 'en' | 'tr' | 'es')}
                disabled={profileSaving}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="en">English</option>
                <option value="tr">Türkçe</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
          )}

          {/* Incident Domains */}
          {activeSection === 'domains' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.incidentDomains.title')}</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">{domains.length}/{workspace?.max_impact_domains ?? 10}</span>
            </div>

            {domainsError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{domainsError}</div>
            )}

            {domainsLoading && domains.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">Yükleniyor...</div>
            )}

            <div className="space-y-3 mb-4">
              {domains.map((domain) => (
                <div key={domain.id} className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  {editingDomain === domain.id ? (
                    <div className="space-y-3">
                      {domainUpdateError && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{domainUpdateError}</div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain Adı</label>
                        <input
                          type="text"
                          value={editDomainName}
                          onChange={(e) => setEditDomainName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama</label>
                        <textarea
                          rows={2}
                          value={editDomainDescription}
                          onChange={(e) => setEditDomainDescription(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Varsayılan Grup</label>
                        <select
                          value={editDomainGroupId}
                          onChange={(e) => setEditDomainGroupId(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Yok</option>
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateDomain(domain.id)}
                          disabled={domainUpdating || !editDomainName}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {domainUpdating ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button
                          onClick={cancelEditDomain}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{domain.name}</div>
                        {domain.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{domain.description}</div>
                        )}
                        {domain.default_group_id && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Varsayılan Grup: {groups.find((g) => g.id === domain.default_group_id)?.name || '-'}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEditDomain(domain)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Düzenle"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveDomain(domain.id)}
                            disabled={deletingDomain === domain.id}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                            title="Sil"
                          >
                            {deletingDomain === domain.id ? (
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canManage && domains.length < (workspace?.max_impact_domains ?? 10) && (
              <div className="space-y-3">
                {domainCreateError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{domainCreateError}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.incidentDomains.domainName')}</label>
                  <input
                    type="text"
                    value={newDomainName}
                    onChange={(e) => setNewDomainName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder={t('settings.incidentDomains.domainNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama</label>
                  <textarea
                    rows={2}
                    value={newDomainDescription}
                    onChange={(e) => setNewDomainDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Opsiyonel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Varsayılan Grup</label>
                  <select
                    value={newDomainGroupId}
                    onChange={(e) => setNewDomainGroupId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="">Yok</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleAddDomain}
                  disabled={domainCreating || !newDomainName}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {domainCreating ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  <span>{t('settings.incidentDomains.addDomain')}</span>
                </button>
              </div>
            )}

            {canManage && domains.length >= (workspace?.max_impact_domains ?? 10) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">{t('settings.incidentDomains.limitReached', { max: workspace?.max_impact_domains ?? 10 })}</p>
            )}
          </div>
          )}

          {/* Projects */}
          {activeSection === 'projects' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Folder className="h-5 w-5 text-indigo-600" />
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.projects.title')}</h2>
            </div>

            {projectsError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{projectsError}</div>
            )}

            {projectsLoading && projects.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">{t('common.loading')}</div>
            )}

            <div className="space-y-3 mb-4">
              {projects.map((project) => (
                <div key={project.id} className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                  {editingProject === project.id ? (
                    <div className="space-y-3">
                      {projectUpdateError && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{projectUpdateError}</div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.name')}</label>
                        <input
                          type="text"
                          value={editProjectName}
                          onChange={(e) => setEditProjectName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description')}</label>
                        <textarea
                          value={editProjectDescription}
                          onChange={(e) => setEditProjectDescription(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateProject(project.id)}
                          disabled={projectUpdating || !editProjectName}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {projectUpdating ? t('settings.projects.saving') : t('common.save')}
                        </button>
                        <button
                          onClick={cancelEditProject}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{project.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({project.project_key})</span>
                        {project.is_default === 1 && (
                          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{t('settings.projects.default')}</span>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleProjectMembers(project.id)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                            title={t('settings.projects.membersTooltip') as string}
                          >
                            <Users className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => startEditProject(project)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title={t('settings.projects.editTooltip') as string}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          {!project.is_default && (
                            <button
                              onClick={() => handleRemoveProject(project.id)}
                              disabled={deletingProject === project.id}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                              title={t('settings.projects.deleteTooltip') as string}
                            >
                              {deletingProject === project.id ? (
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {expandedMembersProjectId === project.id && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-4">
                      {projectMembersError && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{projectMembersError}</div>
                      )}

                      {projectMembersLoading ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                      ) : (
                        <>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('settings.projects.membersTitle')}</h4>
                            {(projectMembersMap[project.id] || []).length === 0 ? (
                              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.projects.noMembers')}</p>
                            ) : (
                              <div className="space-y-2">
                                {(projectMembersMap[project.id] || []).map((m) => (
                                  <div key={m.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{m.user_name || m.user_email || m.user_id}</p>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {m.role_in_project === 'manager' ? t('settings.projects.roleManager') : t('settings.projects.roleMember')}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button
                                        onClick={() => handleSetProjectMemberRole(project.id, m.user_id, m.role_in_project === 'manager' ? 'member' : 'manager')}
                                        disabled={updatingMemberRoleUserId === m.user_id}
                                        className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                                      >
                                        {m.role_in_project === 'manager' ? t('settings.projects.demote') : t('settings.projects.promote')}
                                      </button>
                                      <button
                                        onClick={() => handleRemoveProjectMember(project.id, m.user_id)}
                                        disabled={removingProjectMember === m.user_id}
                                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                        title={t('settings.projects.removeMemberTooltip') as string}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <form onSubmit={(e) => handleInviteToProject(project.id, e)} className="space-y-2">
                            {memberInviteError && (
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{memberInviteError}</div>
                            )}
                            <div className="flex gap-2">
                              <input
                                type="email"
                                value={memberInviteEmail}
                                onChange={(e) => setMemberInviteEmail(e.target.value)}
                                placeholder={t('settings.userInvitation.emailPlaceholder') as string}
                                className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                              <select
                                value={memberInviteRole}
                                onChange={(e) => setMemberInviteRole(e.target.value as UserRole)}
                                className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              >
                                {roleOptions.map((r) => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                              <button
                                type="submit"
                                disabled={memberInviteSending || !memberInviteEmail}
                                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
                              >
                                {memberInviteSending ? t('settings.projects.sending') : t('settings.projects.inviteButton')}
                              </button>
                            </div>
                          </form>

                          {(projectInvitationsMap[project.id] || []).filter((inv) => inv.status === 'pending').length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('settings.userInvitation.pendingInvitations')}</h4>
                              <div className="space-y-2">
                                {(projectInvitationsMap[project.id] || [])
                                  .filter((inv) => inv.status === 'pending')
                                  .map((inv) => (
                                    <div key={inv.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{inv.email}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <span className="text-xs text-gray-500 dark:text-gray-400">{inv.workspace_role}</span>
                                          {getStatusBadge(inv.status)}
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleRevokeProjectInvitation(project.id, inv.id)}
                                        disabled={revokingProjectInvitation === inv.id}
                                        className="ml-2 p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                                        title={t('settings.projects.cancelInviteTooltip') as string}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canManage && (
              <div className="space-y-3">
                {projectCreateError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-sm">{projectCreateError}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.name')}</label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={t('settings.projects.namePlaceholder') as string}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.key')}</label>
                  <input
                    type="text"
                    value={newProjectKey}
                    onChange={(e) => setNewProjectKey(e.target.value.toUpperCase())}
                    maxLength={5}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder={t('settings.projects.keyPlaceholder') as string}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.descriptionOptional')}</label>
                  <textarea
                    value={newProjectDescription}
                    onChange={(e) => setNewProjectDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.membersLabel')}</label>
                  <MultiSelectDropdown
                    options={members.map((m) => ({ id: m.id, label: m.name }))}
                    selectedIds={newProjectMemberIds}
                    onChange={setNewProjectMemberIds}
                    allLabel={t('settings.projects.selectMembers')}
                    searchPlaceholder={t('common.search')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.projects.groupsLabel')}</label>
                  <MultiSelectDropdown
                    options={groups.map((g) => ({ id: g.id, label: g.name }))}
                    selectedIds={newProjectGroupIds}
                    onChange={setNewProjectGroupIds}
                    allLabel={t('settings.projects.selectGroups')}
                    searchPlaceholder={t('common.search')}
                  />
                </div>
                <button
                  onClick={handleAddProject}
                  disabled={projectCreating || !newProjectName || !newProjectKey}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {projectCreating ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  <span>{t('settings.projects.addProject')}</span>
                </button>
              </div>
            )}
          </div>
          )}

          {/* Task Templates */}
          {activeSection === 'templates' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-2 mb-3">
              <LayoutTemplate className="h-5 w-5 text-teal-600" />
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('settings.templates.title')}</h2>
            </div>

            {templatesError && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{templatesError}</div>
            )}

            {templatesLoading && templates.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">Yükleniyor...</div>
            )}

            {!templatesLoading && templates.length === 0 && !templatesError && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.templates.empty')}</p>
            )}

            <div className="space-y-2">
              {templates.map((tpl) => (
                <div key={tpl.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{tpl.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{tpl.title}</div>
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(tpl.id)}
                    disabled={deletingTemplate === tpl.id}
                    className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                    title="Sil"
                  >
                    {deletingTemplate === tpl.id ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* API Documentation — static reference, not tied to any one
              agent/token (compare buildAgentGuide, which IS). Lives here
              so a logged-in user can read it before creating an agent. */}
          {activeSection === 'apiDocs' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 space-y-5 text-sm text-gray-900 dark:text-gray-200">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('settings.apiDocs.title')}</h2>
              <p className="text-gray-600 dark:text-gray-400">{t('settings.apiDocs.intro')}</p>
            </div>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.authTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-1">{t('settings.apiDocs.authText')}</p>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs">Authorization: Bearer cbn_...</pre>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.scopesTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400">tasks:read, tasks:write, incidents:read, incidents:write, documents:read, documents:write</p>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.tasksTitle')}</h3>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs whitespace-pre-wrap">{`GET    /api/tasks
POST   /api/tasks
PUT    /api/tasks/:id/claim
POST   /api/tasks/:id/notes        body: { "note": "..." }
PUT    /api/tasks/:id/complete     body: { "note": "..." }
PUT    /api/tasks/:id/reassign     body: { "assigned_to_user_id" or "assigned_to_group_id", "note": "..." }
POST   /api/tasks/:id/messages     multipart/form-data, field "message"`}</pre>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.incidentsTitle')}</h3>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs whitespace-pre-wrap">{`GET    /api/incidents
POST   /api/incidents
PUT    /api/incidents/:id/start    body: { "assigned_group_id", "investigation_notes" }
POST   /api/incidents/:id/comments body: { "message": "..." }
PUT    /api/incidents/:id/close
PUT    /api/incidents/:id/reassign body: { "assigned_group_id", "note": "..." }`}</pre>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.documentsTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-1">{t('settings.apiDocs.documentsHint')}</p>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs whitespace-pre-wrap">{`GET    /api/projects/:projectId/documents
POST   /api/projects/:projectId/documents                       multipart/form-data, field "file"
GET    /api/projects/:projectId/documents/:documentId/download
DELETE /api/projects/:projectId/documents/:documentId`}</pre>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.webhooksTitle')}</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-1">{t('settings.apiDocs.webhooksHint')}</p>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs whitespace-pre-wrap">{`events: task.note_added, task.reassigned, incident.note_added, incident.reassigned

POST <your URL>
  X-Ceben-Event: task.note_added
  X-Ceben-Signature: sha256=<hex HMAC-SHA256 of the raw body, using your webhook's secret>
  body: { "event": "...", "timestamp": "...", "task": {...}, "note": {...} }`}</pre>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{t('settings.apiDocs.webhooksManageHint')}</p>
            </section>

            <section>
              <h3 className="font-semibold mb-1">{t('settings.apiDocs.exampleTitle')}</h3>
              <pre className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-2 overflow-x-auto text-xs">curl -H "Authorization: Bearer cbn_..." {`${window.location.origin}/api/tasks`}</pre>
            </section>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
